import { GraphInputHelpers, ONE_DAY_MS } from '@/fetching/graphql/GraphInputHelpers';
import { buildEpisodeDisciplineLink } from '@/pages/PatientDetailsPage/PatientSchedulingTab/PatientEpisodeView/routes';
import {
    EpisodeOfCareFilter,
    EpisodeStatus,
    OrderType,
    Ordering,
    ServiceFilter,
    VisitDatetimeField,
    VisitFilter,
    VisitOutcome,
    VisitState,
    VisitWorkflowStep,
} from 'gql-types';
import moment from 'moment';

import {
    DEFAULT_CONTAINER_FILTER_WINDOW_DAYS,
    defaultEpisodeOfCareRowToLink,
    defaultEpisodeVisitsRowToLink,
    defaultVisitsRowToLink,
} from '../definitions';
import {
    EpisodeOfCareDashboardContainer,
    OrderDashboardContainer,
    PatientDashboardContainer,
    RootEntity,
    VisitDashboardContainer,
} from '../types';
import { IDashboardVariationConfig } from './types';

/**
 * ======================================================
 * Reusable Filters
 * ======================================================
 */
const NORTH_STAR_VISITS_START_GTE_FILTER: VisitFilter = {
    startDatetime: {
        gte: GraphInputHelpers.datetime(
            moment().subtract(DEFAULT_CONTAINER_FILTER_WINDOW_DAYS, 'days'),
        ),
    },
};

export const NORTH_STAR_DEATH_VISIT_FILTER: VisitFilter = {
    services: {
        externalIds: {
            type: { exact: 'SERVICE_ID' },
            value: {
                inList: ['HSAIDEDEATH', 'HSRNDEATH', 'HSSCDEATH', 'HSSWDEATH'],
            },
        },
    },
};

const NORTH_STAR_ADMIT_VISIT_SERVICE_FILTER: ServiceFilter = {
    externalIds: {
        value: { inList: ['HSRNADMIT', 'HSRNURGENT'] },
        type: { exact: 'SERVICE_ID' },
    },
};

const IS_ACTIVE_BENEFIT_PERIOD_FILTER: EpisodeOfCareFilter = {
    isActiveByDate: { exact: true },
    isActiveField: { exact: true },
};

const ADMITTED_BENEFIT_PERIOD_FILTER: EpisodeOfCareFilter = {
    ...IS_ACTIVE_BENEFIT_PERIOD_FILTER,
    visits: {
        services: NORTH_STAR_ADMIT_VISIT_SERVICE_FILTER,
        outcome: { exact: VisitOutcome.COMPLETED },
    },
};

const ADMITTED_BENEFIT_PERIOD_1_FILTER: EpisodeOfCareFilter = {
    ...ADMITTED_BENEFIT_PERIOD_FILTER,
    numberInSequence: { exact: 1 },
};

/**
 * ======================================================
 * Container Definitions
 * ======================================================
 */
// Key is intentionally preserved as 'northStarRnAdmitVisits' so that users'
// stored container enable/disable preferences continue to apply after the
// rename to 'Admits Needing Scheduling'.
const NORTH_STAR_ADMIT_VISIT_CONTAINER: VisitDashboardContainer = {
    key: 'northStarRnAdmitVisits',
    label: 'Admits Needing Scheduling',
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            patient: {
                isActive: { exact: true },
            },
            services: NORTH_STAR_ADMIT_VISIT_SERVICE_FILTER,
            state: { exact: VisitState.UNSCHEDULED },
            AND: {
                fulfillmentWindowEndDate: {
                    gte: GraphInputHelpers.date(
                        moment().subtract(DEFAULT_CONTAINER_FILTER_WINDOW_DAYS, 'days'),
                    ),
                },
                OR: {
                    fulfillmentWindowEndDate: {
                        isNull: true,
                    },
                },
            },
        },
        ordering: {
            fulfillmentWindowEndDate: Ordering.ASC,
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No admit visits to schedule.',
    },
    defaultEnabled: true,
    hoverText: 'Admit visits that need scheduling.',
};

const NORTH_STAR_RECENT_ADMITS_CONTAINER: VisitDashboardContainer = {
    key: 'northStarRecentAdmits',
    label: 'Recent Admits',
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            patient: {
                isActive: { exact: true },
                hasEpisodes: {
                    status: { exact: EpisodeStatus.ACTIVE },
                    hasVisits: {
                        disciplineCoveredByCareTeam: { exact: false },
                        // We don't want to ignore the absence of an RN for a completed admit visit,
                        // so unlike the Incomplete Care Team queue, we include completed visits.
                        outcome: {
                            inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED],
                        },
                    },
                },
            },
            services: NORTH_STAR_ADMIT_VISIT_SERVICE_FILTER,
            outcome: { exact: VisitOutcome.COMPLETED },
            startDatetime: {
                gte: GraphInputHelpers.datetime(moment().subtract(1, 'day').startOf('day')),
                lte: GraphInputHelpers.datetime(moment().endOf('day')),
            },
        },
        ordering: {
            startDatetime: Ordering.ASC,
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No recent admits.',
    },
    defaultEnabled: true,
    hoverText: 'Patients with a completed admit visit today or yesterday.',
};

const NEW_FREQUENCIES_CONTAINER: OrderDashboardContainer = {
    key: 'northStarNewFrequencies',
    label: 'New Frequencies',
    tableConfig: {
        entity: RootEntity.ORDER,
        filters: {
            patient: {
                isActive: { exact: true },
            },
            orderType: { exact: OrderType.FREQUENCY },
            isActive: { exact: true },
            maxActiveOccurrences: { gt: 1 },
            activeVisits: {
                outcome: { exact: VisitOutcome.NONE },
                workflowStep: {
                    inList: [VisitWorkflowStep.NEEDS_SERVICE],
                },
            },
        },
        ordering: {
            fulfillmentWindowStartDate: Ordering.ASC,
        },
        rowToLink: ({
            patient: { id: patientId },
            discipline,
            episode,
        }: {
            patient: { id: string };
            discipline: { id: string } | null;
            episode: { id: string } | null;
        }) => {
            return buildEpisodeDisciplineLink(
                {
                    patientId,
                    episodeId: episode?.id,
                },
                discipline?.id,
            );
        },
        emptyTableText: 'No new frequencies to schedule.',
    },
    defaultEnabled: true,
    heavyQuery: true,
    hoverText: 'New frequencies that need scheduling.',
};

const PATIENTS_NEEDING_SCHEDULING: EpisodeOfCareDashboardContainer = {
    key: 'northStarPatientsNeedingScheduling',
    label: 'Patients Needing Scheduling',
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            patient: {
                isActive: { exact: true },
            },
            endDate: { gte: GraphInputHelpers.date(new Date(Date.now() - 14 * ONE_DAY_MS)) },
            hasVisits: {
                needsAssignment: { exact: true },
            },
        },
        ordering: {
            startDate: Ordering.ASC,
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No patients to schedule.',
    },
    defaultEnabled: true,
    heavyQuery: true,
    hoverText: 'Patients that need scheduling.',
};

const EXPIRING_FULFILLMENT_WINDOW_CONTAINER: VisitDashboardContainer = {
    key: 'northStarExpiringFulfillmentWindowVisits',
    label: 'Expiring Fulfillment Windows',
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            patient: {
                isActive: { exact: true },
            },
            needsAssignment: { exact: true },
            latestEndDatetime: {
                gte: GraphInputHelpers.date(
                    new Date(Date.now() - DEFAULT_CONTAINER_FILTER_WINDOW_DAYS * ONE_DAY_MS),
                ),
                lte: GraphInputHelpers.date(new Date(Date.now() + 3 * ONE_DAY_MS)),
            },
        },
        ordering: {
            latestEndDatetime: Ordering.ASC,
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No visits with expiring fulfillment windows.',
    },
    defaultEnabled: true,
    hoverText: 'Visits with expiring fulfillment windows.',
    heavyQuery: true,
};

const NORTH_STAR_INCOMPLETE_CARE_TEAM_CONTAINER: PatientDashboardContainer = {
    key: 'northStarIncompleteCareTeam',
    label: 'Incomplete Care Teams',
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasEpisodes: {
                status: { exact: EpisodeStatus.ACTIVE },
                hasVisits: {
                    disciplineCoveredByCareTeam: { exact: false },
                    outcome: { exact: VisitOutcome.NONE },
                },
            },
        },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients with incomplete care teams.',
    },
    defaultEnabled: true,
    hoverText: 'Patients that need care teams assigned.',
};

const NORTH_STAR_UNASSIGNED_VISIT_CONTAINER: VisitDashboardContainer = {
    key: 'northStarUnassignedVisits',
    label: 'Unassigned',
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            patient: {
                isActive: { exact: true },
            },
            ...NORTH_STAR_VISITS_START_GTE_FILTER,
            needsClinicians: { exact: true },
            outcome: { exact: VisitOutcome.NONE },
        },
        ordering: {
            latestEndDatetime: Ordering.ASC,
        },
        emptyTableText: 'No unassigned visits at this time',
        rowToLink: defaultVisitsRowToLink,
    },
    hoverText: 'Visits in need of a clinician assignment.',
    defaultEnabled: true,
    heavyQuery: true,
};

const NORTH_STAR_DEATH_DISCHARGE_WITH_PENDING_VISITS_CONTAINER: PatientDashboardContainer = {
    key: 'northStarDeathDischargeWithVisits',
    label: 'Deaths/Discharges with Pending Visits',
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: false },
            AND: {
                hasVisits: {
                    outcome: { exact: VisitOutcome.NONE },
                },
            },
        },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No deaths/discharges require visit cancellation at this time.',
    },
    hoverText: 'Visit cancellation required.',
    defaultEnabled: true,
    heavyQuery: true,
};

const NORTH_STAR_INACTIVE_CLINICIAN_VISITS_CONTAINER: VisitDashboardContainer = {
    key: 'northStarInactiveClinicianVisits',
    label: 'Inactive Clinicians',
    tableConfig: {
        entity: RootEntity.VISIT,
        emptyTableText: 'No scheduled visits assigned to inactive clinicians',
        filters: {
            outcome: { exact: VisitOutcome.NONE },
            assignedClinicians: {
                isActive: { exact: false },
            },
            AND: {
                ...NORTH_STAR_VISITS_START_GTE_FILTER,
                OR: {
                    startDatetime: { isNull: true },
                },
            },
        },
        ordering: {
            startDatetime: Ordering.ASC,
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
    },
    hoverText: 'Scheduled visits assigned to inactive clinicians',
    defaultEnabled: true,
    heavyQuery: true,
};

/**
 * RN 14-Day Requirement Container (inactive)
 *
 * Kept as a fallback — currently disabled due to performance (ENG-6942).
 *
 * CMS requires an RN visit every 14 days. This container shows patients who need
 * an RN visit scheduled to stay in compliance.
 *
 * Trigger for Inclusion:
 * - Patient is active with an active benefit period
 * - No RN visit in Completed state within the past 10 days
 * - No RN visits today in a non-terminal or Completed state
 * - No RN visits within the next 4 days in a non-terminal state
 *
 * Trigger for Removal:
 * - An RN visit is in a non-terminal or Completed state today
 * - OR in a non-terminal state within the next 4 days
 * - OR patient is discharged
 *
 * Days Left to Schedule: 0 = today is the last day, -1 (in red) = one day out of compliance
 */
const RN_COMPREHENSIVE_FILTER: VisitFilter = {
    services: {
        externalIds: {
            value: { exact: 'HSRNCOMP' },
        },
    },
};

const _NORTH_STAR_RN_14_DAY_VISIT_CONTAINER: PatientDashboardContainer = {
    key: 'northStarRn14DayVisit',
    label: 'RN 14-Day Requirement',
    defaultEnabled: true,
    hoverText: 'Patients requiring RN visit within 14 day requirement',
    heavyQuery: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        emptyTableText: 'No patients requiring RN visit within 14-day requirement.',
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        filters: {
            // Patient is not discharged
            isActive: { exact: true },
            // Patient has an active benefit period (episode)
            episodes: {
                ...IS_ACTIVE_BENEFIT_PERIOD_FILTER,
            },
            AND: {
                // No RN visit completed in the past 10 days
                NOT: {
                    visits: {
                        ...RN_COMPREHENSIVE_FILTER,
                        outcome: { exact: VisitOutcome.COMPLETED },
                        startDatetime: {
                            range: {
                                start: GraphInputHelpers.datetime(moment().subtract(10, 'days')),
                                end: GraphInputHelpers.datetime(new Date()),
                            },
                        },
                    },
                },
                AND: {
                    // No RN visit today in non-terminal (NONE) or Completed state
                    NOT: {
                        visits: {
                            ...RN_COMPREHENSIVE_FILTER,
                            outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                            startDatetime: {
                                range: {
                                    start: GraphInputHelpers.datetime(moment().startOf('day')),
                                    end: GraphInputHelpers.datetime(moment().endOf('day')),
                                },
                            },
                        },
                    },
                    AND: {
                        // No RN visit in the next 4 days in non-terminal state (NONE = pending/scheduled)
                        NOT: {
                            visits: {
                                ...RN_COMPREHENSIVE_FILTER,
                                outcome: { exact: VisitOutcome.NONE },
                                startDatetime: {
                                    range: {
                                        start: GraphInputHelpers.datetime(
                                            moment().add(1, 'day').startOf('day'),
                                        ),
                                        end: GraphInputHelpers.datetime(
                                            moment().add(4, 'days').endOf('day'),
                                        ),
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
    },
};

const NORTH_STAR_RECERT_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: 'northStarRecert',
    label: 'Recerts',
    defaultEnabled: true,
    hoverText: 'Upcoming Recerts that need scheduling.',
    heavyQuery: true,
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        emptyTableText: 'No Recerts to schedule.',
        filters: {
            patient: {
                isActive: { exact: true },
            },
            // Episode ends within the next 30 days
            endDate: {
                range: {
                    start: GraphInputHelpers.date(new Date()),
                    end: GraphInputHelpers.date(
                        moment().add(DEFAULT_CONTAINER_FILTER_WINDOW_DAYS, 'days'),
                    ),
                },
            },
            AND: {
                ...ADMITTED_BENEFIT_PERIOD_1_FILTER,
                OR: {
                    ...IS_ACTIVE_BENEFIT_PERIOD_FILTER,
                    numberInSequence: { gt: 1 },
                },
            },
            // No scheduled or completed recert visit in this episode
            visits: {
                NOT: {
                    services: {
                        externalIds: {
                            value: { exact: 'HSRNRECERT' },
                        },
                    },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                },
            },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
    },
};

const NORTH_STAR_HUV_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: 'northStarHuv',
    label: 'HUVs',
    defaultEnabled: true,
    hoverText: 'Upcoming HUVs that need scheduling.',
    heavyQuery: true,
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        emptyTableText: 'No HUVs to schedule.',
        filters: {
            isActiveByDate: { exact: true },
            isActiveField: { exact: true },
            patient: {
                isActive: { exact: true },
                hospice: {
                    // HUV1 branch: HOPE start ≤25 days ago, no pending HUV1 in
                    // HOPE+6..15, no completed HUV1 in HOPE+6..25
                    hopeStartDate: {
                        gte: GraphInputHelpers.date(moment().subtract(25, 'days')),
                    },
                    NOT: {
                        hasVisitInHuv1Window: {
                            services: { externalIds: { value: { exact: 'HSRNHUV1' } } },
                            outcome: { exact: VisitOutcome.NONE },
                        },
                        OR: {
                            hasVisitInHopeWindow: {
                                startDay: 6,
                                endDay: 25,
                                visit: {
                                    services: { externalIds: { value: { exact: 'HSRNHUV1' } } },
                                    outcome: { exact: VisitOutcome.COMPLETED },
                                },
                            },
                        },
                    },
                    OR: {
                        // HUV2 branch: HOPE start ≤40 days ago, no pending HUV2 in
                        // HOPE+16..30, no completed HUV2 in HOPE+16..40
                        hopeStartDate: {
                            gte: GraphInputHelpers.date(moment().subtract(40, 'days')),
                        },
                        NOT: {
                            hasVisitInHuv2Window: {
                                services: { externalIds: { value: { exact: 'HSRNHUV2' } } },
                                outcome: { exact: VisitOutcome.NONE },
                            },
                            OR: {
                                hasVisitInHopeWindow: {
                                    startDay: 16,
                                    endDay: 40,
                                    visit: {
                                        services: {
                                            externalIds: { value: { exact: 'HSRNHUV2' } },
                                        },
                                        outcome: { exact: VisitOutcome.COMPLETED },
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
        ordering: {
            startDate: Ordering.ASC,
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
    },
};

const SFV_SERVICE_FILTER: VisitFilter = {
    services: {
        externalIds: {
            type: { exact: 'SERVICE_ID' },
            value: {
                inList: ['HSRNMISSEDSFV', 'HSLPNSFVPRN', 'HSLPNSFV', 'HSRNSFVPRN', 'HSRNSFV'],
            },
        },
    },
};

/**
 * SFV (Symptom Follow-up Visit) Container
 *
 * Shows SIA (Symptom Impact Assessment) visits that need a follow-up SFV visit.
 *
 * Trigger for Inclusion:
 * - SIA represented in myUnity (has sfv_required metadata)
 * - AND SFV is not yet Confirmed/Scheduled
 * - OR Scheduled/Confirmed SFV is now Unassigned or Marked as Missed
 *
 * Trigger for Removal:
 * - SFV visit is Confirmed
 * - OR patient is discharged
 */
const NORTH_STAR_SFV_CONTAINER: VisitDashboardContainer = {
    key: 'northStarSfv',
    label: 'SFVs',
    defaultEnabled: true,
    hoverText: 'Follow-up visits required within 2 days of Symptom Impact Assessments.',
    heavyQuery: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        emptyTableText: 'No SFVs need attention at this time.',
        filters: {
            patient: { isActive: { exact: true } },
            metadata: { hasKey: 'sfv_required' },
            startDatetime: {
                gte: GraphInputHelpers.datetime(moment().startOf('day').subtract(9, 'days')),
            },
            AND: {
                // 1. No completed SFV strictly AFTER the SIA
                relatedVisitCondition: {
                    exists: false,
                    samePatient: true,
                    sameEpisode: true,
                    filter: {
                        ...SFV_SERVICE_FILTER,
                        outcome: { exact: VisitOutcome.COMPLETED },
                    },
                    timeConstraints: [
                        {
                            field: VisitDatetimeField.EFFECTIVE_DATE,
                            relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                            // # > triggering datetime
                            gtOffsetMinutes: 0,
                        },
                    ],
                },
                AND: {
                    // 2. No missed SFV more than 1 day after the SIA (date+1 itself does NOT remove)
                    relatedVisitCondition: {
                        exists: false,
                        samePatient: true,
                        sameEpisode: true,
                        filter: {
                            ...SFV_SERVICE_FILTER,
                            outcome: { exact: VisitOutcome.MISSED },
                        },
                        timeConstraints: [
                            {
                                field: VisitDatetimeField.EFFECTIVE_DATE,
                                relativeTo: VisitDatetimeField.TENTATIVE_DATE,
                                // strictly > triggering + 1 day → next allowed is +2 days
                                gtOffsetMinutes: 1440,
                            },
                        ],
                    },
                    AND: {
                        // # 3. No pending SFV scheduled within 2 days of the SIA (inclusive)
                        relatedVisitCondition: {
                            exists: false,
                            samePatient: true,
                            sameEpisode: true,
                            filter: {
                                ...SFV_SERVICE_FILTER,
                                outcome: { exact: VisitOutcome.NONE },
                            },
                            timeConstraints: [
                                {
                                    field: VisitDatetimeField.EFFECTIVE_DATE,
                                    relativeTo: VisitDatetimeField.TENTATIVE_DATE,
                                    // ≤ triggering + 2 days
                                    lteOffsetMinutes: 2880,
                                },
                            ],
                        },
                    },
                },
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        ordering: {
            startDatetime: Ordering.ASC,
        },
    },
    extraProps: {
        showClinicianColumn: false,
    },
};

/**
 * Patient No-Shows Container
 *
 * Shows visits where the patient did not show up and requires follow-up action.
 *
 * Trigger for Inclusion:
 * - Visit outcome is "Patient Did Not Show"
 * - Visit was not ingested from myUnity with "Missed" status
 * - Visit has not been rescheduled
 *
 * Trigger for Removal:
 * - Visit is assigned to another clinician and its status is Unscheduled, Scheduled, or Confirmed
 */
const NORTH_STAR_PATIENT_NO_SHOWS_CONTAINER: VisitDashboardContainer = {
    key: 'northStarPatientNoShows',
    label: 'Patient No-Shows',
    defaultEnabled: true,
    hoverText: 'Patient no-shows requiring attention',
    heavyQuery: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        emptyTableText: 'No patient no-shows requiring attention.',
        filters: {
            patient: {
                isActive: { exact: true },
            },
            ...NORTH_STAR_VISITS_START_GTE_FILTER,
            // Visit is in review
            state: { exact: VisitState.IN_REVIEW },
            // Visit outcome is "Patient Did Not Show"
            outcome: { exact: VisitOutcome.PATIENT_DID_NOT_SHOW },
        },
        // Sort by visit datetime closest to current datetime
        ordering: {
            startDatetime: Ordering.DESC,
        },
        // Navigate to visit details page
        rowToLink: ({ id }: { id: string }) => `/visits/${id}`,
    },
};

/**
 * ======================================================
 * NorthStar Dashboard Variations
 * ======================================================
 */
// UAT and prod configs are intentionally separate objects even though they're
// currently identical — they've diverged before (e.g. RN 14-day, AH305 team)
// and will likely diverge again during staged rollouts.
export const NORTH_STAR_UAT_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        NORTH_STAR_ADMIT_VISIT_CONTAINER,
        NORTH_STAR_RECENT_ADMITS_CONTAINER,
        NORTH_STAR_INCOMPLETE_CARE_TEAM_CONTAINER,
        NEW_FREQUENCIES_CONTAINER,
        PATIENTS_NEEDING_SCHEDULING,
        NORTH_STAR_UNASSIGNED_VISIT_CONTAINER,
        NORTH_STAR_HUV_CONTAINER,
        NORTH_STAR_SFV_CONTAINER,
        EXPIRING_FULFILLMENT_WINDOW_CONTAINER,
        // makeContainerDefinition({
        //     type: ContainerDefinitionType.OVERDUE_EPISODE_VISIT,
        //     defaultEnabled: true,
        // }),
        NORTH_STAR_DEATH_DISCHARGE_WITH_PENDING_VISITS_CONTAINER,
        NORTH_STAR_INACTIVE_CLINICIAN_VISITS_CONTAINER,
        NORTH_STAR_PATIENT_NO_SHOWS_CONTAINER,
        NORTH_STAR_RECERT_CONTAINER,
        // makeContainerDefinition({
        //     type: ContainerDefinitionType.VISIT_SYNC_ISSUES,
        //     defaultEnabled: true,
        //     overrides: {
        //         label: 'Sync Issues',
        //     },
        // }),
    ],
};

export const NORTH_STAR_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        NORTH_STAR_ADMIT_VISIT_CONTAINER,
        NORTH_STAR_RECENT_ADMITS_CONTAINER,
        NORTH_STAR_INCOMPLETE_CARE_TEAM_CONTAINER,
        NEW_FREQUENCIES_CONTAINER,
        PATIENTS_NEEDING_SCHEDULING,
        NORTH_STAR_UNASSIGNED_VISIT_CONTAINER,
        NORTH_STAR_HUV_CONTAINER,
        NORTH_STAR_SFV_CONTAINER,
        EXPIRING_FULFILLMENT_WINDOW_CONTAINER,
        // makeContainerDefinition({
        //     type: ContainerDefinitionType.OVERDUE_EPISODE_VISIT,
        //     defaultEnabled: true,
        // }),
        NORTH_STAR_DEATH_DISCHARGE_WITH_PENDING_VISITS_CONTAINER,
        NORTH_STAR_INACTIVE_CLINICIAN_VISITS_CONTAINER,
        NORTH_STAR_PATIENT_NO_SHOWS_CONTAINER,
        // TODO(ENG-6942): Re-enable once new backend filter is implemented.
        // The current query shows too many patients and is slow.
        // NORTH_STAR_RN_14_DAY_VISIT_CONTAINER,
        NORTH_STAR_RECERT_CONTAINER,
        // makeContainerDefinition({
        //     type: ContainerDefinitionType.VISIT_SYNC_ISSUES,
        //     defaultEnabled: true,
        //     overrides: {
        //         label: 'Sync Issues',
        //     },
        // }),
    ],
};
